import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card, Form, Input, Button, Tabs, Typography, notification, theme } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import type { AuthUser } from "../context/AuthContext";
import { getSsoIssuerBaseUrl, isDowtexAppOrigin } from "../ssoConfig";
import type { RpcError } from "grpc-web";
import { StatusCode } from "grpc-web";

const { Title, Text } = Typography;
const localized = (isGerman: boolean, german: string, english: string): string => isGerman ? german : english;

const getRedirectDestination = (redirect: string | null): { url: string; issuer: boolean } | null => {
    if (!redirect) return null;
    if (redirect.startsWith("/") && !redirect.startsWith("//")) {
        try {
            return new URL(redirect, window.location.origin).origin === window.location.origin
                ? { url: redirect, issuer: false }
                : null;
        } catch {
            return null;
        }
    }

    try {
        const url = new URL(redirect);
        if (url.protocol !== "https:" || url.username || url.password) return null;
        let issuerOrigin = "";
        try {
            const issuerUrl = new URL(getSsoIssuerBaseUrl());
            if (issuerUrl.protocol === "https:" && !issuerUrl.username && !issuerUrl.password) issuerOrigin = issuerUrl.origin;
        } catch {
            // An invalid issuer URL is not a redirect destination.
        }
        const issuer = url.origin === issuerOrigin;
        return issuer || isDowtexAppOrigin(url.origin) ? { url: url.toString(), issuer } : null;
    } catch {
        return null;
    }
};

const LoginPage: React.FC = () => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const registrationEnabled = process.env.REACT_APP_DOWTEX_REGISTRATION_ENABLED === "true";
    const { token } = theme.useToken();
    const [loginForm] = Form.useForm();
    const [registerForm] = Form.useForm();
    const { user, login, register, isLoading, establishIssuerSession } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [api, contextHolder] = notification.useNotification();
    const [activeTab, setActiveTab] = useState<string>("login");
    const [isRedirecting, setIsRedirecting] = useState(false);
    const initialUser = useRef(user);
    const pageStyle: React.CSSProperties = {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: `radial-gradient(ellipse at 50% -10%, rgba(124, 58, 237, 0.15) 0%, transparent 60%) ${token.colorBgLayout}`,
    };

    const redirectParam = searchParams.get("redirect");

    const followRedirect = useCallback(async (destination: { url: string; issuer: boolean }, sessionUser: AuthUser) => {
        setIsRedirecting(true);
        try {
            if (destination.issuer) await establishIssuerSession(sessionUser);
            window.location.href = destination.url;
        } catch {
            setIsRedirecting(false);
            api.error({
                message: localized(isGerman, "SSO-Anmeldung fehlgeschlagen", "SSO sign-in unavailable"),
                description: localized(isGerman, "Dowtex konnte die SSO-Sitzung nicht herstellen. Bitte versuchen Sie es erneut.", "Dowtex could not establish the sign-in session. Please try again."),
                placement: "top",
            });
        }
    }, [api, establishIssuerSession, isGerman]);

    useEffect(() => {
        if (!initialUser.current || !user || !redirectParam) return;
        initialUser.current = null;
        const destination = getRedirectDestination(redirectParam);
        if (!destination) {
            navigate("/dashboard", { replace: true });
            return;
        }
        void followRedirect(destination, user);
    }, [user, redirectParam, navigate, followRedirect]);

    const onAuthSuccess = async (sessionUser: AuthUser) => {
        const destination = getRedirectDestination(redirectParam);
        if (!destination) {
            navigate("/dashboard");
            return;
        }
        await followRedirect(destination, sessionUser);
    };

    const interpretRpcError = (error: unknown, context: "login" | "register"): string => {
        const rpcError = error as RpcError | undefined;
        if (context === "login" && rpcError?.code === StatusCode.UNAUTHENTICATED) {
            return localized(isGerman, "Benutzername oder Passwort ist falsch.", "Wrong username or password.");
        }
        if (context === "register" && rpcError?.code === StatusCode.ALREADY_EXISTS) {
            return localized(isGerman, "Benutzername ist bereits vergeben.", "Username already taken.");
        }
        const msg = rpcError?.message?.replace(/^\d+\s*/, "").trim();
        if (msg && !msg.toLowerCase().includes("incomplete response")) return msg;
        if (context === "login") return localized(isGerman, "Anmeldung fehlgeschlagen. Bitte versuche es erneut.", "Login failed. Please try again.");
        return localized(isGerman, "Registrierung fehlgeschlagen. Bitte versuche es erneut.", "Registration failed. Please try again.");
    };

    const handleLogin = async (values: { username: string; password: string }) => {
        try {
            const authenticated = await login(values.username.trim(), values.password);
            await onAuthSuccess(authenticated);
        } catch (error) {
            api.error({
                message: localized(isGerman, "Anmeldung fehlgeschlagen", "Login failed"),
                description: interpretRpcError(error, "login"),
                placement: "top",
            });
        }
    };

    const handleRegister = async (values: { username: string; password: string; confirmPassword: string }) => {
        if (values.password !== values.confirmPassword) {
            registerForm.setFields([{ name: "confirmPassword", errors: [localized(isGerman, "Passwörter stimmen nicht überein.", "Passwords do not match.")] }]);
            return;
        }
        try {
            await register(values.username.trim(), values.password);
            api.success({
                message: localized(isGerman, "Konto erstellt!", "Account created!"),
                description: localized(isGerman, "Du kannst dich jetzt anmelden.", "You can now log in."),
                placement: "top",
            });
            registerForm.resetFields();
            setActiveTab("login");
        } catch (error) {
            api.error({
                message: localized(isGerman, "Registrierung fehlgeschlagen", "Registration failed"),
                description: interpretRpcError(error, "register"),
                placement: "top",
            });
        }
    };

    if (isRedirecting) {
        return (
            <main className="sf-login" style={pageStyle}>
                {contextHolder}
                <div aria-live="polite" style={{ color: token.colorText }}>
                    <Text>{localized(isGerman, "Weiterleitung läuft …", "Redirecting …")}</Text>
                </div>
            </main>
        );
    }

    return (
        <main className="sf-login" style={pageStyle}>
            {contextHolder}
            <div style={{ width: "100%", maxWidth: 420 }}>
                <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <img src="/logo192.png" alt="Dowtex" style={{ width: 60, height: 60, marginBottom: 12, borderRadius: 14 }} />
                    <Title level={1} style={{ margin: 0 }}>Dowtex</Title>
                    <Text type="secondary">{localized(isGerman, "Melde dich an, um deine Apps zu nutzen", "Sign in to access your apps")}</Text>
                </div>
                <Card style={{
                    borderTop: `2px solid ${token.colorPrimary}`,
                    boxShadow: "0 24px 64px rgba(0,0,0,0.15), 0 4px 16px rgba(124,58,237,0.12)",
                }}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        centered more={{ icon: localized(isGerman, "Weitere Anmeldeoptionen", "More sign-in options") }}
                        items={[
                            {
                                key: "login",
                                label: localized(isGerman, "Anmelden", "Login"),
                                children: (
                                    <Form form={loginForm} layout="vertical" onFinish={handleLogin} requiredMark={false}>
                                        <Form.Item name="username" label={localized(isGerman, "Benutzername", "Username")} rules={[{ required: true, message: localized(isGerman, "Bitte gib deinen Benutzernamen ein.", "Please enter your username.") }]}>
                                            <Input prefix={<UserOutlined />} placeholder={localized(isGerman, "Benutzername", "Username")} autoComplete="username" />
                                        </Form.Item>
                                        <Form.Item name="password" label={localized(isGerman, "Passwort", "Password")} rules={[{ required: true, message: localized(isGerman, "Bitte gib dein Passwort ein.", "Please enter your password.") }]}>
                                            <Input.Password prefix={<LockOutlined />} placeholder={localized(isGerman, "Passwort", "Password")} autoComplete="current-password" />
                                        </Form.Item>
                                        <Button type="primary" htmlType="submit" block loading={isLoading}>
                                            {localized(isGerman, "Anmelden", "Log in")}
                                        </Button>
                                    </Form>
                                ),
                            },
                            ...(registrationEnabled ? [{
                                key: "register",
                                label: localized(isGerman, "Registrieren", "Register"),
                                children: (
                                    <Form form={registerForm} layout="vertical" onFinish={handleRegister} requiredMark={false}>
                                        <Form.Item name="username" label={localized(isGerman, "Benutzername", "Username")} rules={[{ required: true, message: localized(isGerman, "Bitte gib einen Benutzernamen ein.", "Please enter a username.") }, { min: 3, message: localized(isGerman, "Der Benutzername muss mindestens 3 Zeichen lang sein.", "Username must be at least 3 characters.") }]}>
                                            <Input prefix={<UserOutlined />} placeholder={localized(isGerman, "Benutzername", "Username")} autoComplete="username" />
                                        </Form.Item>
                                        <Form.Item name="password" label={localized(isGerman, "Passwort", "Password")} rules={[{ required: true, message: localized(isGerman, "Bitte gib ein Passwort ein.", "Please enter a password.") }, { min: 6, message: localized(isGerman, "Das Passwort muss mindestens 6 Zeichen lang sein.", "Password must be at least 6 characters.") }]}>
                                            <Input.Password prefix={<LockOutlined />} placeholder={localized(isGerman, "Passwort", "Password")} autoComplete="new-password" />
                                        </Form.Item>
                                        <Form.Item name="confirmPassword" label={localized(isGerman, "Passwort bestätigen", "Confirm password")} rules={[{ required: true, message: localized(isGerman, "Bitte bestätige dein Passwort.", "Please confirm your password.") }]}>
                                            <Input.Password prefix={<LockOutlined />} placeholder={localized(isGerman, "Passwort bestätigen", "Confirm password")} autoComplete="new-password" />
                                        </Form.Item>
                                        <Button type="primary" htmlType="submit" block loading={isLoading}>
                                            {localized(isGerman, "Registrieren", "Register")}
                                        </Button>
                                    </Form>
                                ),
                            }] : []),
                        ]}
                    />
                </Card>
            </div>
        </main>
    );
};

export default LoginPage;
